import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

import '@awesome.me/webawesome/dist/components/popover/popover.js';

import '../ns-icon/ns-icon.js';
import './ns-service-status-pill.js';
import './ns-trust-score-light.js';

import { VerificationState, VerificationFactor } from './verification-factors.js';
import { TrustScoreController } from '../ns-services/ns-trust-score-service.js';

export type { VerificationState, VerificationFactor };

const STATE_LABELS: Record<VerificationState, string> = {
  verified: 'Validated',
  invalid: 'Not verified yet',
  warning: 'Warning',
};

const STATE_DESCRIPTIONS: Record<VerificationState, string> = {
  verified: 'Fully connected and verified',
  invalid: 'Process not started',
  warning: 'Process started but not yet completed',
};

@customElement('ns-verification-badge')
export class NSVerificationBadge extends LitElement {
  @property({ type: String })
  state: VerificationState = 'invalid';

  @property({ type: String })
  label?: string;

  @property({ attribute: false })
  factors?: VerificationFactor[];

  // With it, the popover shows the company's published trust score, read when the popover first opens
  @property({ type: String })
  companyId?: string;

  @state()
  private __open = false;

  private readonly __trustScore = new TrustScoreController(this, () => this.companyId, { lazy: true });

  static styles = [
    css`
      :host {
        display: inline-flex;
        align-items: center;
        justify-content: center;

        --icon-height: 28px;
        --icon-width: 28px;
      }

      .trigger {
        display: inline-flex;
        min-width: 24px;
        min-height: 24px;
        padding: 0;
        border: 0;
        background: none;
        color: inherit;
        cursor: pointer;
      }

      wa-popover {
        --max-width: 320px;
        --arrow-size: 0;
      }

      wa-popover::part(body) {
        padding: var(--wa-space-m);
        border: 0;
        border-radius: var(--wa-border-radius-l);
        background: var(--wa-color-surface-default);
        color: var(--ns-theme-type);
        box-shadow: 0 8px 24px color-mix(in srgb, var(--ns-theme-black) 35%, transparent);
        font-family: var(--ns-theme-font-family);
        font-weight: 300;
      }

      .card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--wa-space-s);

        margin-bottom: var(--wa-space-s);
      }

      .card-title {
        margin: 0;
        font-weight: 700;
        font-size: 1.1em;
      }

      .close-button {
        flex: none;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        padding: 0;
        border: 0;
        border-radius: var(--wa-border-radius-circle);
        background: color-mix(in srgb, var(--ns-theme-type) 10%, transparent);
        color: inherit;
        font-size: var(--wa-font-size-s);
        line-height: 1;
        cursor: pointer;
      }

      .close-button:hover,
      .close-button:focus {
        background: color-mix(in srgb, var(--ns-theme-type) 20%, transparent);
      }

      .trust-score-row {
        display: flex;
        align-items: center;
        gap: var(--wa-space-xs);
        padding: var(--wa-space-xs);
        border-radius: var(--wa-border-radius-l);
        background: var(--wa-color-surface-lowered);
        margin-bottom: var(--wa-space-2xs);
      }

      .trust-score-icon {
        flex: none;
        box-sizing: border-box;
        width: 28px;
        height: 28px;
        padding: var(--wa-space-3xs);
        border-radius: var(--wa-border-radius-circle);
        background: var(--ns-theme-type);
      }

      .trust-score-label {
        flex: 1;
        min-width: 0;
        font-weight: 700;
      }

      /* As tall as the row's logo */
      ns-trust-score-light {
        --ns-trust-score-light-size: 28px;

        margin-inline-end: var(--wa-space-2xs);
      }

      .factor-rows {
        display: flex;
        flex-direction: column;
        gap: var(--wa-space-2xs);
      }

      .factor-row {
        padding: var(--wa-space-xs);
        border-radius: var(--wa-border-radius-l);

        background: var(--wa-color-surface-lowered);
      }

      .factor-description {
        margin-top: var(--wa-space-2xs);
        font-size: 0.85em;
        opacity: 0.7;
      }
    `,
  ];

  // The published score as a traffic light, its number, state and date only in the light's tooltip; nothing without a companyId
  private __renderTrustScore() {
    if (!this.companyId) return nothing;
    const reading = this.__trustScore.value ?? (this.__trustScore.failed ? 'failed' : 'loading');

    return html`
      <div class="trust-score-row">
        <img class="trust-score-icon" src="/images/logo-icon.png" alt="" />
        <div class="trust-score-label">Trust Score</div>
        <ns-trust-score-light .reading=${reading}></ns-trust-score-light>
      </div>
    `;
  }

  private __renderDetails(factors: VerificationFactor[]) {
    return html`
      <div class="card-header">
        <h2 class="card-title">Verified Status</h2>
        <button type="button" class="close-button" aria-label="Close" data-popover="close">✕</button>
      </div>

      ${this.__renderTrustScore()}

      <div class="factor-rows">
        ${repeat(
          factors,
          (factor) => factor.key,
          (factor) => html`
            <div class="factor-row">
              <ns-service-status-pill label="${factor.label}" state="${factor.state}"></ns-service-status-pill>
              <div class="factor-description">${STATE_DESCRIPTIONS[factor.state]}</div>
            </div>
          `,
        )}
      </div>
    `;
  }

  render() {
    if (!this.factors) {
      return html`
        <ns-icon
          role="img"
          aria-label="${this.label ?? STATE_LABELS[this.state]}"
          title="${this.label ?? STATE_LABELS[this.state]}"
          icon="verification/${this.state}"
        ></ns-icon>
      `;
    }

    return html`
      <button
        type="button"
        id="trigger"
        class="trigger"
        aria-expanded="${this.__open}"
        aria-label="${this.label ?? STATE_LABELS[this.state]}, view verification details"
        @click="${(event: MouseEvent) => event.stopPropagation()}"
      >
        <ns-icon icon="verification/${this.state}" aria-hidden="true"></ns-icon>
      </button>
      <wa-popover
        for="trigger"
        placement="bottom-start"
        @wa-show="${(event: Event) => {
          // The trust score light's tooltip shows and hides inside the popover, and its events bubble out of it
          if (event.target !== event.currentTarget) return;
          this.__open = true;
          this.__trustScore.request();
        }}"
        @wa-hide="${(event: Event) => {
          if (event.target === event.currentTarget) this.__open = false;
        }}"
      >
        ${this.__renderDetails(this.factors)}
      </wa-popover>
    `;
  }
}
